import React, { useMemo, useState } from 'react';
import { BarChart3Icon, Building2Icon, DownloadIcon, TagIcon } from 'lucide-react';
import { downloadCsv } from '../../data/csv';
import { type DepartmentAnalysisClassification, type DepartmentAnalysisDepartment, type DepartmentAnalysisJob, type DepartmentBreakdownKey, type DepartmentBreakdownRow } from './reportingTypes';
type DepartmentAnalysisProps = {
  breakdownKey: DepartmentBreakdownKey;
  departments: DepartmentAnalysisDepartment[];
  jobs: DepartmentAnalysisJob[];
  classifications: Record<string, DepartmentAnalysisClassification>;
  onBreakdownKeyChange: (key: DepartmentBreakdownKey) => void;
};
type BreakdownDetails = {
  label: string;
  description: string;
};
type Category = {
  label: string;
  color: string;
};
const BREAKDOWN_DETAILS: Record<DepartmentBreakdownKey, BreakdownDetails> = {
  status: {
    label: 'Status',
    description: 'Compare department work by its current project status.'
  },
  source: {
    label: 'Project Source',
    description: 'Compare department work by where each project originated.'
  },
  sector: {
    label: 'Project Sector',
    description: 'Compare department work by project sector.'
  }
};
const STATUS_COLORS: Record<string, string> = {
  Ongoing: '#0F766E',
  Quote: '#D97706',
  'PCC in progress': '#2563EB',
  Closed: '#16A34A',
  Lost: '#DC2626'
};
const SECTOR_COLORS: Record<string, string> = {
  Commercial: '#2563EB',
  Residential: '#0F766E',
  'Mixed-use': '#7C3AED',
  Retail: '#D97706'
};
const SEGMENT_COLORS = ['#0F766E', '#2563EB', '#D97706', '#7C3AED', '#DB2777', '#475569'];
export function DepartmentAnalysis({
  breakdownKey,
  departments,
  jobs,
  classifications,
  onBreakdownKeyChange
}: DepartmentAnalysisProps) {
  const rows = useMemo(() => createDepartmentRows(departments, jobs, classifications, breakdownKey), [breakdownKey, classifications, departments, jobs]);
  const [countView, setCountView] = useState<'table' | 'chart'>('table');
  const [valueView, setValueView] = useState<'table' | 'chart'>('chart');
  const activeOption = BREAKDOWN_DETAILS[breakdownKey];
  const categories = getCategories(rows);
  return <section className="space-y-6" data-id="element-95" data-mp="30btbf5mdegyn">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6" aria-labelledby="department-display-metric-heading" data-id="element-96" data-mp="30cuo1ej1bbih">
        <div data-id="element-97" data-mp="27ndxyniw06pf">
          <h3 id="department-display-metric-heading" className="text-base font-semibold text-slate-900" data-id="element-98" data-mp="27nnteqmpb62i">
            Display metric
          </h3>
          <p className="mt-1 text-sm text-slate-500" data-id="element-99" data-mp="2hllcz0tm8eos">
            Choose how department activity is grouped across the analysis.
          </p>
        </div>
        <div className="mt-4 flex max-w-full gap-2 overflow-x-auto pb-1" role="group" aria-label="Department analysis metric" data-id="element-100" data-mp="2mlzk8gne7klr">
          <DepartmentMetricPill active={breakdownKey === 'status'} icon={<BarChart3Icon className="h-4 w-4" data-id="element-102" data-mp="1mcq5yokdy8tu" />} label="Status" onClick={() => onBreakdownKeyChange('status')} data-id="element-101" data-mp="1teako651q8u" />
          <DepartmentMetricPill active={breakdownKey === 'source'} icon={<TagIcon className="h-4 w-4" data-id="element-104" data-mp="1cl3v4tbymbrm" />} label="Project Source" onClick={() => onBreakdownKeyChange('source')} data-id="element-103" data-mp="1hdx2loytby7e" />
          <DepartmentMetricPill active={breakdownKey === 'sector'} icon={<Building2Icon className="h-4 w-4" data-id="element-106" data-mp="12lwb5napzivj" />} label="Project Sector" onClick={() => onBreakdownKeyChange('sector')} data-id="element-105" data-mp="12o8xsdrfahex" />
        </div>
      </section>

      {rows.length === 0 ? <EmptyDepartmentAnalysis data-id="element-107" data-mp="oopsrttwrm6u" /> : <>
          <AnalysisViewToggle title="Project assignments" view={countView} onViewChange={setCountView} description={`Project assignment count by department and ${activeOption.label.toLowerCase()}.`} measure="count" rows={rows} categories={categories} groupingLabel={activeOption.label} data-id="element-108" data-mp="oqaovnvvfd6t" />
          <AnalysisViewToggle title="Contract value" view={valueView} onViewChange={setValueView} description={`Allocated contract value by department and ${activeOption.label.toLowerCase()}.`} measure="value" rows={rows} categories={categories} groupingLabel={activeOption.label} data-id="element-109" data-mp="1kq4q6q9xwhte" />
        </>}
    </section>;
}
function DepartmentMetricPill({
  active,
  icon,
  label,
  onClick
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" aria-pressed={active} onClick={onClick} className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 ${active ? 'border-[#14466B] bg-[#14466B] text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-110" data-mp="t0066sq3dwsp">
      {icon}
      {label}
    </button>;
}
function BreakdownTable({
  title,
  description,
  measure,
  rows,
  categories,
  embedded = false
}: {
  title: string;
  description: string;
  measure: 'count' | 'value';
  rows: DepartmentBreakdownRow[];
  categories: Category[];
  embedded?: boolean;
}) {
  const headingId = `${measure}-table-heading`;
  const total = rows.reduce((sum, row) => sum + (measure === 'count' ? row.count : row.value), 0);
  const table = <div className="overflow-x-auto" data-id="element-111" data-mp="s3etbiymtfca">
      <table className="w-full min-w-[680px] text-left" data-id="element-112" data-mp="n5d4siifsntf">
        <thead className="bg-slate-50" data-id="element-113" data-mp="n5vae5qzoee0">
          <tr className="border-b border-slate-200" data-id="element-114" data-mp="12247b0neqfn9">
            <th scope="col" className="sticky left-0 z-10 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 md:px-6" data-id="element-115" data-mp="122m17qmlkhs0">
              Department
            </th>
            {categories.map(category => <th key={category.label} scope="col" className="whitespace-nowrap px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-116" data-mp="196c5g76j26jo">
                <span className="inline-flex items-center gap-1.5" data-id="element-117" data-mp="1ixgk15sajpdz">
                  <span className="h-2 w-2 rounded-sm" style={{
                backgroundColor: category.color
              }} aria-hidden="true" data-id="element-118" data-mp="1ntotq3npyqdr" />
                  {category.label}
                </span>
              </th>)}
            <th scope="col" className="whitespace-nowrap px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 md:px-6" data-id="element-119" data-mp="21s588grtpl6g">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-id="element-120" data-mp="3w350wypb6f0u">
          {rows.map(row => <tr key={row.id} data-id="element-121" data-mp="3rb3guqlwfk09">
              <th scope="row" className="sticky left-0 z-10 bg-white px-5 py-4 text-sm font-medium text-slate-900 md:px-6" data-id="element-122" data-mp="3r9shd2zhkyey">
                {row.name}
              </th>
              {categories.map(category => {
            const segment = row.segments.find(item => item.label === category.label);
            const value = segment ? measure === 'count' ? segment.count : segment.value : 0;
            return <td key={category.label} className="px-5 py-4 text-right text-sm tabular-nums text-slate-600" data-id="element-123" data-mp="3xhq9z4sicce">
                    {measure === 'count' ? value : formatCurrency(value)}
                  </td>;
          })}
              <td className="px-5 py-4 text-right text-sm font-semibold tabular-nums text-slate-900 md:px-6" data-id="element-124" data-mp="3eb2pl7nqch6b">
                {measure === 'count' ? row.count : formatCurrency(row.value)}
              </td>
            </tr>)}
        </tbody>
        <tfoot className="border-t border-slate-200 bg-slate-50" data-id="element-125" data-mp="39daxodn34v24">
          <tr data-id="element-126" data-mp="39fndol9a8vcj">
            <th scope="row" className="sticky left-0 z-10 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 md:px-6" data-id="element-127" data-mp="39fdwnn67tyiz">
              Total
            </th>
            {categories.map(category => {
            const categoryTotal = rows.reduce((sum, row) => {
              const segment = row.segments.find(item => item.label === category.label);
              return sum + (segment ? measure === 'count' ? segment.count : segment.value : 0);
            }, 0);
            return <td key={category.label} className="px-5 py-3 text-right text-sm font-semibold tabular-nums text-slate-700" data-id="element-128" data-mp="24dmeeju8ibc0">
                  {measure === 'count' ? categoryTotal : formatCurrency(categoryTotal)}
                </td>;
          })}
            <td className="px-5 py-3 text-right text-sm font-semibold tabular-nums text-slate-900 md:px-6" data-id="element-129" data-mp="1lftfdsmqeljw">
              {measure === 'count' ? total : formatCurrency(total)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>;
  if (embedded) return table;
  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-labelledby={headingId} data-id="element-130" data-mp="1vgc6n8gml4bu">
      <div className="border-b border-slate-100 px-5 py-4 md:px-6" data-id="element-131" data-mp="36txsajna0nsm">
        <h3 id={headingId} className="text-base font-semibold text-slate-900" data-id="element-132" data-mp="mluz3tf5a2zs">
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500" data-id="element-133" data-mp="hn05rxjjww1f">{description}</p>
      </div>
      {table}
    </section>;
}
function AnalysisViewToggle({
  title,
  view,
  onViewChange,
  description,
  measure,
  rows,
  categories,
  groupingLabel
}: {
  title: string;
  view: 'table' | 'chart';
  onViewChange: (view: 'table' | 'chart') => void;
  description: string;
  measure: 'count' | 'value';
  rows: DepartmentBreakdownRow[];
  categories: Category[];
  groupingLabel: string;
}) {
  const slug = measure === 'count' ? 'project-assignments' : 'contract-value';
  const headingId = `${slug}-analysis-heading`;
  const panelId = `${slug}-analysis-panel`;
  const exportBreakdown = () => {
    downloadCsv<DepartmentBreakdownRow>(`department-${slug}-export.csv`, [{
      label: 'Department',
      value: row => row.name
    }, {
      label: measure === 'count' ? 'Total assignments' : 'Total contract value',
      value: row => measure === 'count' ? row.count : row.value
    }, ...categories.map(category => ({
      label: category.label,
      value: (row: DepartmentBreakdownRow) => {
        const segment = row.segments.find(item => item.label === category.label);
        return measure === 'count' ? segment?.count || 0 : segment?.value || 0;
      }
    }))], rows);
  };
  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-labelledby={headingId} data-id="element-134" data-mp="25l2grob08jp0">
      <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between md:px-6" data-id="element-135" data-mp="17ylj2hyd042q">
        <h3 id={headingId} className="text-base font-semibold text-slate-900" data-id="element-136" data-mp="1hv82d2b1en2n">
          {description}
        </h3>
        <div className="flex flex-wrap items-center gap-2" data-id="element-137" data-mp="1mukpzp9qfy6a">
          <div className="inline-flex w-fit rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label={`${title} view`} data-id="element-138" data-mp="1mryjzu2hpqq6">
            <button type="button" aria-pressed={view === 'table'} aria-controls={panelId} onClick={() => onViewChange('table')} className={`min-h-8 rounded-md px-3 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 ${view === 'table' ? 'bg-white text-[#14466B] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-139" data-mp="2y7dgkwqsegkr">
              Table
            </button>
            <button type="button" aria-pressed={view === 'chart'} aria-controls={panelId} onClick={() => onViewChange('chart')} className={`min-h-8 rounded-md px-3 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 ${view === 'chart' ? 'bg-white text-[#14466B] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-140" data-mp="fgz3m3v0qx42">
              Bar chart
            </button>
          </div>
          <button type="button" onClick={exportBreakdown} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title={`Export ${title.toLowerCase()} as CSV`} data-id="element-141" data-mp="3nvr8ac0ifi2c"><DownloadIcon className="h-4 w-4" data-id="element-142" data-mp="33vzcwy3ssppq" />Export CSV</button>
        </div>
      </div>
      <div id={panelId} role="region" aria-labelledby={headingId} data-id="element-143" data-mp="38tr23kd25xq8">
        {view === 'table' ? <BreakdownTable title={`${title} table`} description={description} measure={measure} rows={rows} categories={categories} embedded data-id="element-144" data-mp="dz4xiy8skc4" /> : <VerticalBarChart title={`${title} bar chart`} description={description} measure={measure} rows={rows} categories={categories} groupingLabel={groupingLabel} embedded data-id="element-145" data-mp="3rjqofzhikypy" />}
      </div>
    </section>;
}
function VerticalBarChart({
  title,
  description,
  measure,
  rows,
  categories,
  groupingLabel,
  embedded = false
}: {
  title: string;
  description: string;
  measure: 'count' | 'value';
  rows: DepartmentBreakdownRow[];
  categories: Category[];
  groupingLabel: string;
  embedded?: boolean;
}) {
  const headingId = `${measure}-bar-chart-heading`;
  const maxValue = Math.max(1, ...rows.map(row => measure === 'count' ? row.count : row.value));
  const chart = <>
      <div className={`${embedded ? 'px-5 pt-5 md:px-6 md:pt-6' : 'mt-6'}`} role="img" aria-label={chartAriaLabel(rows, measure, groupingLabel)} data-id="element-146" data-mp="3abr4tf0j3lx5">
        <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-147" data-mp="35e7rchbnnd69">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-148" data-mp="30cs88ut1fgqi">
            Grouped by {groupingLabel}
          </span>
          <div className="flex flex-wrap gap-x-4 gap-y-2" aria-label={`${title} ${groupingLabel} legend`} data-id="element-149" data-mp="1oyekdpyab1ah">
            {categories.map(category => <span key={category.label} className="inline-flex items-center gap-2 text-xs font-medium text-slate-600" data-id="element-150" data-mp="4hxazlip8bl0">
                <span className="h-3 w-3 rounded-sm" style={{
              backgroundColor: category.color
            }} aria-hidden="true" data-id="element-151" data-mp="ed8ro9do3sve" />
                {category.label}
              </span>)}
          </div>
        </div>

        <div className="overflow-x-auto pb-1" data-id="element-152" data-mp="s55ebxwkhake">
          <div className="min-w-[620px]" data-id="element-153" data-mp="s3vfv7lsy0d8">
            <div className="grid grid-cols-[52px_minmax(0,1fr)]" data-id="element-154" data-mp="x51bpeare3qz">
              <div className="relative h-60 border-b border-slate-200" data-id="element-155" data-mp="x2or0tdm9fa8">
                {[1, 0.75, 0.5, 0.25].map(ratio => <span key={ratio} className="absolute right-2 -translate-y-1/2 text-[10px] font-medium tabular-nums text-slate-400" style={{
                bottom: `${ratio * 100}%`
              }} data-id="element-156" data-mp="2ywecbr5p1ph8">
                    {formatChartAxisValue(maxValue * ratio, measure)}
                  </span>)}
                <span className="absolute bottom-0 right-2 translate-y-1/2 text-[10px] font-medium tabular-nums text-slate-400" data-id="element-157" data-mp="2rv12wocme5k1">
                  0
                </span>
              </div>
              <div className="relative h-60 border-b border-slate-200" data-id="element-158" data-mp="30sbe69pftuoy">
                {[25, 50, 75, 100].map(position => <span key={position} className="absolute inset-x-0 border-t border-dashed border-slate-200" style={{
                bottom: `${position}%`
              }} aria-hidden="true" data-id="element-159" data-mp="3arsgch5hdqg6" />)}
                <div className="relative z-10 grid h-full grid-flow-col auto-cols-fr items-end gap-3 px-3" data-id="element-160" data-mp="3jm73t70607ad">
                  {rows.map(row => {
                  const total = measure === 'count' ? row.count : row.value;
                  return <div key={row.id} className="flex min-w-[86px] flex-col items-center justify-end" data-id="element-161" data-mp="gzqx8w155uux">
                        <span className="mb-2 rounded bg-white/90 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-800 shadow-sm ring-1 ring-slate-200" data-id="element-162" data-mp="711hb8st2khb">
                          {measure === 'count' ? total : formatCurrency(total)}
                        </span>
                        <div className="flex h-52 w-full max-w-[58px] items-end" aria-hidden="true" data-id="element-163" data-mp="21f3ax55l07g">
                          <div className="flex w-full flex-col-reverse overflow-hidden rounded-t-md border-x border-t border-slate-200 shadow-sm" style={{
                        height: `${total / maxValue * 100}%`
                      }} data-id="element-164" data-mp="3kfoc3ia8zqzl">
                            {categories.map(category => {
                          const segment = row.segments.find(item => item.label === category.label);
                          const value = segment ? measure === 'count' ? segment.count : segment.value : 0;
                          if (!value) return null;
                          return <span key={category.label} className="w-full shrink-0 border-t border-white/40 first:border-t-0" style={{
                            height: `${value / total * 100}%`,
                            backgroundColor: category.color
                          }} title={`${row.name}: ${category.label}, ${measure === 'count' ? value : formatCurrency(value)}`} data-id="element-165" data-mp="3c11sejpqug2d" />;
                        })}
                          </div>
                        </div>
                      </div>;
                })}
                </div>
              </div>
              <div data-id="element-166" data-mp="2o4x0fn20mahl" />
              <div className="grid grid-flow-col auto-cols-fr gap-3 px-3 pt-2" data-id="element-167" data-mp="2o2axk0q2kj5p">
                {rows.map(row => <span key={row.id} className="min-w-[86px] text-center text-xs font-medium leading-4 text-slate-600" data-id="element-168" data-mp="1vfq50wxuy10k">
                    {row.name}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
  if (embedded) return chart;
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6" aria-labelledby={headingId} data-id="element-169" data-mp="2a6778iibguyi">
      <h3 id={headingId} className="text-base font-semibold text-slate-900" data-id="element-170" data-mp="1hn9vreed56r7">
        {title}
      </h3>
      <p className="mt-1 text-sm text-slate-500" data-id="element-171" data-mp="39vcp77ctz9og">{description}</p>
      {chart}
    </section>;
}
function EmptyDepartmentAnalysis() {
  return <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-14 text-center" data-id="element-172" data-mp="3exlik4tm8dr3">
      <Building2Icon className="mx-auto h-9 w-9 text-slate-300" data-id="element-173" data-mp="3jsr7zdm88ajx" />
      <h3 className="mt-4 text-base font-semibold text-slate-900" data-id="element-174" data-mp="3nvnhmetsgwbq">
        No department data available
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500" data-id="element-175" data-mp="2r7r6hph4et7w">
        Add active departments in Settings and assign departments to projects to
        populate this analysis.
      </p>
    </div>;
}
function createDepartmentRows(settingsDepartments: DepartmentAnalysisDepartment[], jobs: DepartmentAnalysisJob[], classifications: Record<string, DepartmentAnalysisClassification>, breakdownKey: DepartmentBreakdownKey): DepartmentBreakdownRow[] {
  const departmentByName = new Map<string, DepartmentAnalysisDepartment>();
  settingsDepartments.forEach(department => departmentByName.set(department.name, department));
  jobs.flatMap(job => job.departments).filter(Boolean).forEach(name => {
    if (!departmentByName.has(name)) {
      departmentByName.set(name, {
        id: `assigned-${normalizeId(name)}`,
        name
      });
    }
  });
  const records = Array.from(departmentByName.values()).map(department => ({
    ...department,
    count: 0,
    value: 0,
    segments: new Map<string, {
      count: number;
      value: number;
    }>()
  }));
  const recordByName = new Map(records.map(record => [record.name, record]));
  jobs.forEach(job => {
    const assignedDepartments = Array.from(new Set(job.departments.filter(Boolean)));
    if (assignedDepartments.length === 0) return;
    const allocatedValue = job.value / assignedDepartments.length;
    const segmentLabel = classificationValue(job, classifications[job.id], breakdownKey);
    assignedDepartments.forEach(departmentName => {
      const record = recordByName.get(departmentName);
      if (!record) return;
      record.count += 1;
      record.value += allocatedValue;
      const segment = record.segments.get(segmentLabel) || {
        count: 0,
        value: 0
      };
      segment.count += 1;
      segment.value += allocatedValue;
      record.segments.set(segmentLabel, segment);
    });
  });
  return records.map(record => ({
    id: record.id,
    name: record.name,
    count: record.count,
    value: record.value,
    segments: Array.from(record.segments.entries()).map(([label, values]) => ({
      label,
      ...values,
      color: STATUS_COLORS[label] || SECTOR_COLORS[label] || colorForSegment(label)
    })).sort((left, right) => right.value - left.value)
  })).sort((left, right) => right.value - left.value || left.name.localeCompare(right.name));
}
function getCategories(rows: DepartmentBreakdownRow[]): Category[] {
  return Array.from(new Set(rows.flatMap(row => row.segments.map(segment => segment.label)))).map(label => ({
    label,
    color: STATUS_COLORS[label] || SECTOR_COLORS[label] || colorForSegment(label)
  }));
}
function classificationValue(job: DepartmentAnalysisJob, classification: DepartmentAnalysisClassification | undefined, key: DepartmentBreakdownKey) {
  if (key === 'status') return statusLabel(job.status);
  if (key === 'source') return classification?.source || 'Unclassified source';
  return classification?.sector || 'Unclassified sector';
}
function statusLabel(status: DepartmentAnalysisJob['status']) {
  const labels: Record<DepartmentAnalysisJob['status'], string> = {
    quote: 'Quote',
    pcc_in_progress: 'PCC in progress',
    ongoing: 'Ongoing',
    closed: 'Closed',
    lost: 'Lost'
  };
  return labels[status];
}
function chartAriaLabel(rows: DepartmentBreakdownRow[], measure: 'count' | 'value', groupingLabel: string) {
  const measureLabel = measure === 'count' ? 'project count' : 'contract value';
  return `${measureLabel} vertical bar chart by department, grouped by ${groupingLabel}. ${rows.map(row => `${row.name}: ${measure === 'count' ? `${row.count} project assignments` : formatCurrency(row.value)}`).join('. ')}`;
}
function colorForSegment(label: string) {
  const index = Array.from(label).reduce((sum, character) => sum + character.charCodeAt(0), 0) % SEGMENT_COLORS.length;
  return SEGMENT_COLORS[index];
}
function normalizeId(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
function formatChartAxisValue(value: number, measure: 'count' | 'value') {
  if (measure === 'count') return Math.round(value).toString();
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}m`;
  if (value >= 1000) return `£${Math.round(value / 1000)}k`;
  return `£${Math.round(value)}`;
}
function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(value);
}